import { useQuery, useQueryClient } from 'react-query';
import {
  getMetricNamesForExperimentKeys,
  loadExpDetailsPersonalTemplate
} from '../api/expDetailsTemplateApi';
import useExpDetailsRouteParams from '../useExpDetailsRouteParams';
import { addAutoGeneratedMetricPanels } from '@experiment-details/utils/panels';
import { expDetailsTemplateKeys } from '../api/exp-details-template-key-factory';
import {
  ExperimentTemplateData,
  ExperimentTemplatePreview,
  SelectedExpDetailsTemplate
} from '@experiment-details/types';
import useExpTemplateList from './useExpTemplateList';
import {
  useAutogenCurvesSections,
  useAutogenDataframeSections
} from '@API/project';
import { mergeTemplateSections } from '@experiment-management-shared/components/Charts/utils/sections';
import { PanelSection } from '@experiment-management-shared/types';

const updateTemplateSections = (
  template: ExperimentTemplateData,
  sections: PanelSection[]
) => ({
  ...template,
  chartTemplate: {
    ...template.chartTemplate,
    panels: {
      ...template.chartTemplate.panels,
      sections: mergeTemplateSections(
        template.chartTemplate.panels.sections,
        sections
      )
    }
  }
});

const useExpDetailsPersonalTemplate = (): SelectedExpDetailsTemplate => {
  const queryClient = useQueryClient();
  const { isLoading: isTemplateListLoading } = useExpTemplateList();

  const {
    experimentKey,
    projectId,
    experimentKeys
  } = useExpDetailsRouteParams();

  const enabled = Boolean(experimentKey && projectId && !isTemplateListLoading);

  const {
    data: personalTemplateData,
    isLoading: isPersonalTemplateLoading,
    isRefetching: isPersonalTemplateRefetching
  } = useQuery(
    expDetailsTemplateKeys.personalTemplate(experimentKey, projectId),
    async () => {
      // needed to avoid closure while invalidating
      const list =
        (queryClient.getQueryData(
          expDetailsTemplateKeys.templateList(projectId)
        ) as ExperimentTemplatePreview[]) || [];
      const savedPersonalTemplateData = list.find(t => t.personalView);

      const data = await loadExpDetailsPersonalTemplate({
        experimentKey,
        projectId,
        templateId: savedPersonalTemplateData?.templateId
      });

      return data;
    },
    {
      enabled
    }
  );

  const isAutogenerateDisabled =
    personalTemplateData?.chartTemplate.panels.autoGenerate === false;

  const metricKeysQueryEnabled = Boolean(
    personalTemplateData &&
      experimentKeys.length >= 0 &&
      !isPersonalTemplateRefetching &&
      !isAutogenerateDisabled
  );

  const {
    isLoading: isMetricKeysLoading,
    isRefetching: isMetricKeysRefetching
  } = useQuery(
    expDetailsTemplateKeys.metricKeys(experimentKeys),
    async ({ signal }) => {
      if (experimentKeys?.length === 0 || isAutogenerateDisabled) {
        return [];
      }

      return getMetricNamesForExperimentKeys(experimentKeys, signal);
    },
    {
      enabled: metricKeysQueryEnabled,
      onSuccess: async metricNames => {
        if (!personalTemplateData) return;
        const metricsTemplate = await addAutoGeneratedMetricPanels({
          template: personalTemplateData,
          metricNames
        });

        queryClient.setQueryData(
          expDetailsTemplateKeys.personalTemplate(experimentKey, projectId),
          metricsTemplate
        );
      }
    }
  );

  const loadingMap = {
    personalView: isPersonalTemplateLoading,
    metricNames: isMetricKeysLoading
  };

  const isLoading = Object.values(loadingMap).some(isLoading => isLoading);

  const assetPanelsEnabled = Boolean(
    !isLoading &&
      experimentKeys.length &&
      !isAutogenerateDisabled &&
      !isMetricKeysRefetching
  );

  const {
    isSuccess: isAutogenCurvesPanelsSuccess,
    isLoading: isAutogenCurvesPanelsLoading
  } = useAutogenCurvesSections(
    {
      experimentKeys,
      existingSections: personalTemplateData?.chartTemplate.panels.sections
    },
    {
      enabled: assetPanelsEnabled,
      onSuccess: async curveSections => {
        if (!personalTemplateData) return;
        const template = updateTemplateSections(
          personalTemplateData,
          curveSections
        );

        queryClient.setQueryData(
          expDetailsTemplateKeys.personalTemplate(experimentKey, projectId),
          template
        );
      }
    }
  );

  const {
    isLoading: isAutogenDataframePanelsLoading
  } = useAutogenDataframeSections(
    {
      experimentKeys,
      existingSections: personalTemplateData?.chartTemplate.panels.sections
    },
    {
      enabled: Boolean(assetPanelsEnabled && isAutogenCurvesPanelsSuccess),
      onSuccess: async tableSections => {
        if (!personalTemplateData) return;
        const template = updateTemplateSections(
          personalTemplateData,
          tableSections
        );

        queryClient.setQueryData(
          expDetailsTemplateKeys.personalTemplate(experimentKey, projectId),
          template
        );
      }
    }
  );

  const isAssetPanelsLoading =
    isAutogenDataframePanelsLoading && isAutogenCurvesPanelsLoading;

  return {
    templateData: personalTemplateData,
    isLoading,
    isAssetPanelsLoading
  };
};

export default useExpDetailsPersonalTemplate;
